import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api, type Me } from '../api/client.ts';
import { isNative, setMediaTicket } from '../api/origin.ts';
import { setRecordingQuality } from '../lib/capture.ts';
import { flushClientErrors } from '../lib/errorReporter.ts';
import { disableNotifications, forgetNativePush } from '../lib/push.ts';
import { forgetNativeShare } from '../lib/nativeShare.ts';
import { setImportQuality } from '../lib/videoCompress.ts';

interface AuthState {
  user: Me | null;
  /** True until the initial /auth/me check resolves — avoids a login flash. */
  loading: boolean;
  /**
   * The session check could not be made at all, as opposed to having been made
   * and answered «нет».
   *
   * The difference is the whole reason this exists: a 401 is an answer and the
   * login screen is the right thing to draw, but a phone on a dead mobile
   * signal has learnt nothing about its session, and signing it out would throw
   * away a perfectly good one. So that case is a third state, and what it draws
   * is «нет связи» with something to press.
   */
  unreachable: boolean;
  /** Asks again. What the button on that screen does. */
  recheck: () => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Replaces the held identity with a fresh one. Saving a profile answers with
   * the whole payload, so there is nothing to refetch — and a second trip to
   * /auth/me would only be a chance for the two to disagree.
   */
  applyIdentity: (me: Me) => void;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * How often to ask again while the answer is «никто не ответил».
 *
 * Well under the twenty seconds one attempt may take (TIMEOUT_MS in
 * api/client.ts) and the latch below is what keeps that from meaning four
 * requests in flight at once: this is «try as soon as the last one gave up»,
 * not a request every five seconds. Only ever running while the screen says
 * there is no connection, so it costs a phone that is working nothing at all.
 */
const RECHECK_MS = 5_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  /** A check is in flight; see recheck. */
  const asking = useRef(false);

  /**
   * Status 0 is «никто не ответил» — offline, or nothing came back inside the
   * deadline in api/client.ts. Everything else the server can say about a
   * session, 401 included, is an answer, and an answer means the login screen.
   *
   * At most one at a time, because this is now called from four places and they
   * do not arrive politely spaced: a phone waking up reports the network back,
   * the app returning to the screen and a due timer inside the same second, and
   * a person who presses «Повторить» does it while all three are pending. The
   * latch is what lets the retry below be written as «ask again» without every
   * caller having to know about the others — and it is a ref rather than
   * `loading`, which is state and therefore a render behind.
   */
  const recheck = useCallback(() => {
    if (asking.current) return;
    asking.current = true;
    setLoading(true);
    api
      .me()
      .then((me) => {
        setUser(me);
        setUnreachable(false);
      })
      .catch((error: unknown) => {
        const noAnswer = error instanceof ApiError && error.status === 0;
        setUnreachable(noAnswer);
        if (!noAnswer) setUser(null);
      })
      .finally(() => {
        asking.current = false;
        setLoading(false);
      });
  }, []);

  useEffect(recheck, [recheck]);

  /**
   * Asks again, on its own, for as long as nobody has answered.
   *
   * Without this the «Нет связи» screen was a dead end with a button on it: the
   * check ran once as the app came up, and a signal that returned a second
   * later changed nothing until somebody pressed. That is most of what
   * /auth/me files as status 0 in client_errors — not a broken server but a
   * launch that happened during a reconnect, on a phone whose WebView had just
   * been rebuilt around a new renderer and which nobody was watching at that
   * moment anyway. A screen that repairs itself is the difference between a bad
   * ten seconds and «опять не работает».
   *
   * Three signals, and the interval is the least of them: `online` and coming
   * back on screen are the moments the answer has genuinely changed, and a
   * phone that reports neither is what the timer is for. It runs only while
   * `unreachable` — an answered session, even a 401, has nothing to retry.
   *
   * The button stays. It is what somebody who has been staring at this for
   * fifteen seconds needs to press, and pressing it now costs nothing: the
   * latch folds it into whichever attempt is already running.
   */
  useEffect(() => {
    if (!unreachable) return;

    const again = () => recheck();
    const onVisible = () => {
      if (document.visibilityState === 'visible') again();
    };

    window.addEventListener('online', again);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(again, RECHECK_MS);
    return () => {
      window.removeEventListener('online', again);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [unreachable, recheck]);

  /**
   * How good a recording is — sound and picture both — is the server's decision
   * and the browser's job: the encoder runs here, and it is not part of React at
   * all, so the settings are handed to it as soon as there is a session, on a
   * fresh load and on a login alike. An admin changing one reaches this device on
   * its next load.
   *
   * The same is true, and matters more, of what a picked video is re-encoded to:
   * that transcode is what decides whether 50 МБ or 7.5 МБ crosses the network.
   */
  useEffect(() => {
    if (!user) return;
    setRecordingQuality(user.recording);
    setImportQuality(user.videoImport);
  }, [user]);

  /**
   * A session appearing is the moment queued error reports become sendable —
   * the endpoint is authenticated, so anything filed on the login screen, or
   * after a session expired, has been sitting on a 401 until now. The reporter
   * retries on its own timer too; this is what stops it taking a minute.
   */
  useEffect(() => {
    if (user) void flushClientErrors();
  }, [user]);

  /**
   * Keeps the native build's media ticket current — the credential every `<img>`
   * and `<video>` carries in its query, because an element cannot send a header.
   *
   * Refreshed when the app comes back on screen rather than on a timer: the
   * ticket is good for at least a day, and the launch it has to be valid at is
   * the one after a phone has had the app closed for a week. A failure is left
   * alone; the last ticket is very likely still good, and the next time anyone
   * looks at the app is soon enough to try again.
   *
   * Does nothing at all on the web, where the cookie already covers these.
   */
  useEffect(() => {
    if (!isNative || !user) return;

    const refresh = () => {
      void api
        .mediaTicket()
        .then(({ ticket }) => setMediaTicket(ticket))
        .catch(() => {});
    };

    refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user]);

  const login = useCallback(async (username: string, password: string) => {
    setUser(await api.login(username, password));
  }, []);

  const logout = useCallback(async () => {
    // Signing out is switching notifications off on this device, and it has to
    // happen while there is still a session to unsubscribe with. The server
    // drops the subscription this session registered as well (see
    // forgetSubscriptionsForSession) — belt and braces, because each covers
    // what the other cannot: this one reaches rows written before the server
    // recorded a session against them, and that one survives a phone with no
    // signal at the moment somebody signs out. Without either, the device goes
    // on receiving the messages of an account nobody is signed into here.
    await disableNotifications().catch(() => {});
    await api.logout().catch(() => {});
    // The bearer the notification buttons were given goes with the session it
    // belonged to. Nothing else on this phone holds one, and a credential that
    // outlives its session is the one thing that arrangement must not do —
    // see configureNativePush.
    await forgetNativePush();
    // And the same credential the iOS share sheet posts with, for the same
    // reason — it is the same session id, in a second place.
    await forgetNativeShare();
    setUser(null);
  }, []);

  const applyIdentity = useCallback((me: Me) => setUser(me), []);

  const value = useMemo(
    () => ({ user, loading, unreachable, recheck, login, logout, applyIdentity }),
    [user, loading, unreachable, recheck, login, logout, applyIdentity],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
